import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useObligations, useCreateObligation } from '../../../platform/api/hooks';
import { asList, asRecord, str, getMeta } from '../../../platform/api/utils';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';
import {
  type CreateForm,
  TYPE_LABELS,
  STATUS_LABELS,
  STATUS_VARIANTS,
  VISIBILITY_LABELS,
  VISIBILITY_VARIANTS,
  createFormSchema,
  Th,
  Td,
  PaginationButton,
  Field,
  Skeleton,
  inputCss,
  primaryBtn,
  secondaryBtn,
  modalOverlay,
  modalStyle,
} from './obligation-list.support';
import { onlyIf, usePermissions } from '../../../platform/auth/permissions';
let idCounter = 0;
export function ObligationList() {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const params: Record<string, unknown> = { page, limit: 20 };
  if (search) params.search = search;
  if (statusFilter) params.status = statusFilter;
  if (typeFilter) params.type = typeFilter;
  const { data, isLoading, isError } = useObligations(params);
  const createObligation = useCreateObligation();
  const obligations = asList(data);
  const meta = getMeta(data);
  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);
  const handleStatusFilter = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(1);
  }, []);
  const handleTypeFilter = useCallback((value: string) => {
    setTypeFilter(value);
    setPage(1);
  }, []);
  const form = useForm<CreateForm>({
    resolver: zodResolver(createFormSchema),
    defaultValues: {
      title: '',
      type: 'HOUSE_OBLIGATION',
      visibility: 'INTERNAL',
      description: '',
      expectedDate: '',
      restrictedNotes: '',
      eventId: '',
      responsibleIds: [],
    },
  });
  function handleCreate(data: CreateForm) {
    createObligation.mutate({
      title: data.title,
      type: data.type,
      visibility: data.visibility,
      description: data.description === '' ? undefined : data.description,
      restrictedNotes: data.restrictedNotes === '' ? undefined : data.restrictedNotes,
      expectedDate: data.expectedDate ? new Date(data.expectedDate).toISOString() : undefined,
      // empty string breaks Prisma FK — omit when blank
      eventId: data.eventId === '' ? undefined : data.eventId,
      responsibleIds: data.responsibleIds?.length ? data.responsibleIds : undefined,
    });
    setShowCreate(false);
    form.reset();
  }
  function fmtDate(v: unknown): string {
    if (typeof v === 'string' && v.length >= 10) return v.substring(0, 10);
    if (v instanceof Date) return v.toISOString().substring(0, 10);
    return '—';
  }
  if (isError) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
        <PageHeader title="Obrigações" subtitle="Acompanhamento de obrigações administrativas" />
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', fontSize: '0.875rem', margin: 0 }}>
            Não foi possível carregar a lista de obrigações.
          </p>
        </Section>
      </div>
    );
  }
  const totalPages = Number(meta.totalPages) || 1;
  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
      <PageHeader
        title="Obrigações"
        subtitle={`${str(meta.total)} obrigações registradas`}
        action={onlyIf(can('obligations.create'), {
          label: '+ Nova obrigação',
          onClick: () => {
            setShowCreate(true);
          },
        })}
      />
      {/* Filtros */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Buscar por título..."
          value={search}
          onChange={(e) => {
            handleSearch(e.target.value);
          }}
          style={{
            flex: 1,
            maxWidth: 300,
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            handleStatusFilter(e.target.value);
          }}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
            background: '#fff',
            minWidth: 180,
          }}
        >
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => {
            handleTypeFilter(e.target.value);
          }}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
            background: '#fff',
            minWidth: 180,
          }}
        >
          <option value="">Todos os tipos</option>
          {Object.entries(TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {/* Skeleton */}
      {isLoading && <Skeleton />}

      {/* Tabela */}
      {!isLoading && (
        <Section title="Obrigações">
          {obligations.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                color: 'var(--faint)',
                fontSize: '0.875rem',
              }}
            >
              {search || statusFilter || typeFilter
                ? 'Nenhuma obrigação encontrada com esses filtros.'
                : 'Nenhuma obrigação registrada ainda.'}
            </div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--line-soft)' }}>
                    <Th>Título</Th>
                    <Th>Tipo</Th>
                    <Th>Situação</Th>
                    <Th>Data prevista</Th>
                    <Th>Visibilidade</Th>
                    <Th>Resp.</Th>
                  </tr>
                </thead>
                <tbody>
                  {obligations.map((raw) => {
                    const o = asRecord(raw);
                    const st = str(o.status);
                    const tp = str(o.type);
                    const vis = str(o.visibility);
                    const respCount = (o._count as Record<string, unknown> | undefined)
                      ?.responsibles as number | undefined;
                    return (
                      <tr
                        key={str(o.id, String(idCounter++))}
                        onClick={() => {
                          void navigate(`/obrigacoes/${String(o.id)}`);
                        }}
                        style={{
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--soft)',
                          transition: 'background 0.15s',
                        }}
                      >
                        <Td>
                          <span style={{ fontWeight: 500, color: 'var(--green)' }}>
                            {str(o.title)}
                          </span>
                        </Td>
                        <Td>
                          <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                            {TYPE_LABELS[tp] ?? tp}
                          </span>
                        </Td>
                        <Td>
                          <Badge
                            label={STATUS_LABELS[st] ?? st}
                            variant={STATUS_VARIANTS[st] ?? 'default'}
                          />
                        </Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {fmtDate(o.expectedDate)}
                        </Td>
                        <Td>
                          <Badge
                            label={VISIBILITY_LABELS[vis] ?? vis}
                            variant={VISIBILITY_VARIANTS[vis] ?? 'default'}
                          />
                        </Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {respCount ?? 0}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Paginação */}
              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    marginTop: '1rem',
                  }}
                >
                  <PaginationButton
                    disabled={page <= 1}
                    onClick={() => {
                      setPage(page - 1);
                    }}
                    label="<"
                  />
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <PaginationButton
                      key={p}
                      active={p === page}
                      onClick={() => {
                        setPage(p);
                      }}
                      label={String(p)}
                    />
                  ))}
                  <PaginationButton
                    disabled={page >= totalPages}
                    onClick={() => {
                      setPage(page + 1);
                    }}
                    label=">"
                  />
                </div>
              )}
            </>
          )}
        </Section>
      )}

      {/* Modal: Nova obrigação */}
      {showCreate && (
        <div
          style={modalOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreate(false);
            }
          }}
        >
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem' }}>Nova obrigação</h3>
            <p
              style={{
                fontSize: '0.7rem',
                color: 'var(--warning)',
                margin: '0 0 1rem',
                padding: '0.4rem 0.6rem',
                background: 'var(--warning-soft)',
                borderRadius: 8,
                border: '1px solid var(--warning-soft)',
              }}
            >
              Não registre fundamento, passo ritualístico ou segredo litúrgico.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <Field label="Título" error={form.formState.errors.title?.message}>
                <input {...form.register('title')} style={inputCss} />
              </Field>
              <Field label="Tipo">
                <select {...form.register('type')} style={inputCss}>
                  {Object.entries(TYPE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Visibilidade">
                <select {...form.register('visibility')} style={inputCss}>
                  {Object.entries(VISIBILITY_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Descrição">
                <textarea
                  {...form.register('description')}
                  style={{ ...inputCss, minHeight: 60, resize: 'vertical' }}
                />
              </Field>
              <Field label="Data prevista">
                <input {...form.register('expectedDate')} type="date" style={inputCss} />
              </Field>
              <div
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  justifyContent: 'flex-end',
                  marginTop: '0.5rem',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowCreate(false);
                    form.reset();
                  }}
                  style={secondaryBtn}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void form.handleSubmit(handleCreate)();
                  }}
                  disabled={createObligation.isPending}
                  style={primaryBtn}
                >
                  {createObligation.isPending ? 'Criando...' : 'Criar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
